import { useCallback, useEffect, useState } from "react";
import { gbp, JERSEY_SEAT_LICENCE_PENCE } from "../lib/pricing";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { theme, card, btnPrimary, inputStyle, floatingSheet, countTag } from "../theme";
import { DimensionHeading, IndexTabs, RulerProgress } from "../components/Drawing";
import { api, apiErrorMessage } from "../api/client";
import { InviteHistoryTable, revokeInvite } from "../components/InviteHistoryTable";
import type { InviteSummary } from "../components/InviteHistoryTable";
import { InviteAuditorModal } from "../components/InviteModals";
import { ConsultantClientView } from "./ConsultantClientView";
import { clientPortalPath } from "../nav/consultantNav";
import { safeHref } from "../utils/safeHref";
import { StandardsWatchTab } from "../components/StandardsWatchTab";
import { ControlMappingsAdminTab } from "../components/ControlMappingsAdminTab";
import { BugReportsAdminTab } from "../components/BugReportsAdminTab";
import { BureauChangeRequestsAdminTab } from "../components/BureauChangeRequestsAdminTab";

import { cbPalette, cbSafe } from "../lib/colourBlind";
// ── Types ─────────────────────────────────────────────────────────────────

interface AttentionItem {
  type: "critical_score" | "never_assessed";
  org: { id: string; name: string; consultancyName: string | null; score: number | null; frameworkId: string | null };
}

interface PlatformStats {
  consultancies: number;
  clients: number;
  consultants: number;
  auditors: number;
  compliantClients: number;
  partialClients: number;
  atRiskClients: number;
  neverAssessedClients: number;
  attentionCount: number;
  attentionItems: AttentionItem[];
  recentActivity: Array<{ id: string; orgName: string; score: number | null; completedAt: string | null }>;
}

interface ConsultancySummary {
  id: string;
  name: string;
  slug: string;
  platformOwner: boolean;
  /** Licensed to run Jersey edition clients, billed per consultant seat (backend lib/pricing.ts). */
  jerseyEditionLicensed?: boolean;
  contactEmail: string | null;
  website: string | null;
  createdAt: string;
  _count: { organizations: number; users: number };
}

interface ConsultancyDetail extends ConsultancySummary {
  users: Array<{ id: string; displayName: string; email: string; isActive: boolean }>;
  organizations: Array<{
    id: string;
    name: string;
    sector: string | null;
    size: string | null;
    serviceTier: { name: string };
    latestScore: number | null;
    lastChecked: string | null;
    frameworks: string[];
  }>;
}

interface AdminOrgDetail {
  id: string;
  name: string;
  sector: string | null;
  size: string | null;
  createdAt: string;
  consultancy: { id: string; name: string } | null;
  serviceTier: { name: string; level: number };
  frameworks: string[];
  userCount: number;
  latestScore: number | null;
  lastChecked: string | null;
  recentChecks: Array<{ id: string; score: number | null; completedAt: string | null }>;
  soaCounts: { compliant: number; gap: number; inProgress: number; notStarted: number };
  findings: Array<{ id: string; controlId: string; text: string; status: string; createdAt: string; bureau: string }>;
  upcomingEvents: Array<{ id: string; title: string; eventDate: string; kind: string; frameworkId: string | null }>;
}

interface OrgSummary {
  id: string;
  name: string;
  sector: string | null;
  createdAt: string;
  releasedAt: string | null;
  consultancy: { id: string; name: string } | null;
  serviceTier: { name: string; level: number };
  userCount: number;
  latestScore: number | null;
  frameworks: string[];
}

interface ConsultantUser {
  id: string;
  displayName: string;
  email: string;
  isActive: boolean;
  createdAt: string;
  consultancy: { id: string; name: string; platformOwner: boolean } | null;
}

interface Bureau {
  id: string;
  name: string;
  accreditation: string | null;
  country: string | null;
  _count: { organizations: number; users: number };
}

interface AuditorUser {
  id: string;
  displayName: string;
  email: string;
  isActive: boolean;
  createdAt: string;
  bureau: { id: string; name: string } | null;
}

type AdminTab = "overview" | "consultancies" | "clients" | "consultants" | "auditors" | "revenue" | "invites" | "partners" | "bureau-review" | "bureau-changes" | "standards-watch" | "control-mappings" | "bug-reports";

// ── Shared helpers ────────────────────────────────────────────────────────

const FW_SHORT: Record<string, string> = {
  iso27001: "27001", iso9001: "9001", iso42001: "42001",
  soc2: "SOC 2", cyber_essentials: "CE", cyber_essentials_plus: "CE+", pci_dss: "PCI",
  iasme_ca_sole_trader: "IASME ST", iasme_ca_micro: "IASME M",
  iasme_ca_small: "IASME S", iasme_ca_medium_large: "IASME M/L",
};

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB");
}

function scoreColor(score: number | null) {
  if (score === null) return theme.color.faint;
  if (score >= 75) return theme.color.successText;
  if (score >= 50) return theme.color.warningText;
  return theme.color.dangerText;
}

const th: React.CSSProperties = {
  padding: "8px 12px", textAlign: "left", fontSize: 10,
  color: theme.color.faint, textTransform: "uppercase",
  letterSpacing: "0.06em", fontWeight: 600,
};

const td: React.CSSProperties = { padding: "10px 12px", fontSize: 13 };

function Pill({ label, muted }: { label: string; muted?: boolean }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 4,
      background: muted ? theme.color.panel : theme.color.accentBg,
      border: `1px solid ${theme.color.border}`,
      color: muted ? theme.color.muted : theme.color.accentText,
    }}>
      {label}
    </span>
  );
}

function ActiveBadge({ active }: { active: boolean }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4,
      background: active ? cbSafe("#052e16") : theme.color.panel,
      color: active ? theme.color.successText : theme.color.faint,
    }}>
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function SearchInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder ?? "Search…"}
      style={{ ...inputStyle, width: 240, marginBottom: 16 }}
    />
  );
}

function EmptyRow({ cols, message }: { cols: number; message: string }) {
  return (
    <tr>
      <td colSpan={cols} style={{ ...td, textAlign: "center", color: theme.color.faint, padding: "32px 12px" }}>
        {message}
      </td>
    </tr>
  );
}

// ── Admin org detail view (client drill-down) ─────────────────────────────

type OrgDetailTab = "overview" | "controls" | "findings" | "schedule";

const FINDING_COLOR: Record<string, string> = {
  open: theme.color.dangerText,
  acknowledged: theme.color.warningText,
  resolved: theme.color.successText,
};
const FINDING_BG: Record<string, string> = cbPalette({
  open: "rgba(220,38,38,0.07)",
  acknowledged: "rgba(180,83,9,0.07)",
  resolved: "rgba(22,163,74,0.07)",
});
const FINDING_LABEL: Record<string, string> = {
  open: "Open",
  acknowledged: "Acknowledged",
  resolved: "Resolved",
};
const EVENT_KIND_COLOR: Record<string, string> = {
  audit: theme.color.dangerText,
  review: theme.color.accentText,
  renewal: theme.color.warningText,
  meeting: theme.color.successText,
};

function AdminOrgDetailView({ org, tab, setTab }: {
  org: AdminOrgDetail;
  tab: OrgDetailTab;
  setTab: (t: OrgDetailTab) => void;
}) {
  const sc = org.latestScore;
  const col = scoreColor(sc);
  const totalSoa = org.soaCounts.compliant + org.soaCounts.gap + org.soaCounts.inProgress + org.soaCounts.notStarted;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {/* Score hero */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
            {org.size ?? ""}{org.size && org.sector ? " · " : ""}{org.sector ?? ""}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, fontSize: 12, color: theme.color.subtle }}>
            <span>Tier: <strong style={{ color: theme.color.text }}>{org.serviceTier.name}</strong></span>
            <span>Users: <strong style={{ color: theme.color.text }}>{org.userCount}</strong></span>
            {org.lastChecked && <span>Last AI check: <strong style={{ color: theme.color.text }}>{fmtDate(org.lastChecked)}</strong></span>}
            {!org.lastChecked && <span style={{ color: theme.color.faint }}>Never assessed</span>}
          </div>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 10 }}>
            {org.frameworks.map((f) => <Pill key={f} label={FW_SHORT[f] ?? f} muted />)}
            {org.frameworks.length === 0 && <span style={{ fontSize: 12, color: theme.color.faint }}>No frameworks</span>}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0, minWidth: 90 }}>
          <div style={{ fontSize: 36, fontWeight: 800, color: col, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
            {sc !== null ? `${sc}%` : "—"}
          </div>
          <div style={{ fontSize: 10, color: sc === null ? theme.color.faint : col, marginTop: 3 }}>
            {sc === null ? "No data" : sc >= 75 ? "Compliant" : sc >= 50 ? "Partial" : "Non-compliant"}
          </div>
          {sc !== null && (
            <RulerProgress value={sc} fill={col} label="Compliance score" style={{ width: 80, marginLeft: "auto", marginTop: 6 }} />
          )}
        </div>
      </div>

      {/* Tabs */}
      <IndexTabs
        items={(["overview", "controls", "findings", "schedule"] as OrgDetailTab[]).map((t) => ({
          id: t,
          label: t.charAt(0).toUpperCase() + t.slice(1),
          count: t === "findings" && org.findings.length > 0 ? org.findings.length : undefined,
        }))}
        value={tab}
        onChange={setTab}
        ground="var(--c-panel)"
        style={{ marginBottom: 16 }}
      />

      {/* Overview */}
      {tab === "overview" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            {[
              { label: "Compliance score", value: sc !== null ? `${sc}%` : "—", color: col },
              { label: "Compliant controls", value: String(org.soaCounts.compliant), color: theme.color.successText },
              { label: "Gaps to close", value: String(org.soaCounts.gap), color: theme.color.dangerText },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ background: theme.color.bg, border: `1px solid ${theme.color.border}`, borderLeft: `3px solid ${color}`, borderRadius: 3, padding: "10px 12px" }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
              </div>
            ))}
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Recent AI checks</div>
            {org.recentChecks.length === 0 ? (
              <div style={{ fontSize: 12, color: theme.color.faint }}>No AI checks run yet.</div>
            ) : org.recentChecks.map((ck) => (
              <div key={ck.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: `1px solid ${theme.color.border}`, fontSize: 12 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: scoreColor(ck.score), flexShrink: 0 }} />
                <span style={{ fontWeight: 600, color: theme.color.text }}>{ck.score !== null ? `${ck.score}%` : "—"}</span>
                <span style={{ color: theme.color.faint, flex: 1 }}>{ck.completedAt ? fmtDate(ck.completedAt) : "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Controls */}
      {tab === "controls" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {totalSoa === 0 ? (
            <div style={{ fontSize: 12, color: theme.color.faint }}>No SOA entries yet — assessment not started.</div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {[
                  { label: "Compliant", count: org.soaCounts.compliant, color: theme.color.successText, bg: cbSafe("#052e16"), border: cbSafe("rgba(22,163,74,.25)") },
                  { label: "Gap", count: org.soaCounts.gap, color: theme.color.dangerText, bg: cbSafe("rgba(220,38,38,.07)"), border: cbSafe("rgba(220,38,38,.25)") },
                  { label: "In progress", count: org.soaCounts.inProgress, color: theme.color.warningText, bg: "rgba(180,83,9,.07)", border: "rgba(180,83,9,.25)" },
                  { label: "Not started", count: org.soaCounts.notStarted, color: theme.color.faint, bg: theme.color.bg, border: theme.color.border },
                ].map(({ label, count, color, bg, border }) => (
                  <div key={label} style={{ background: bg, border: `1px solid ${border}`, borderRadius: 3, padding: "14px 16px", textAlign: "center" }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{count}</div>
                    <div style={{ fontSize: 10, color, marginTop: 4, fontWeight: 600 }}>{label}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11, color: theme.color.faint, textAlign: "center" }}>
                {totalSoa} controls total · full assessment in client portal
              </div>
            </>
          )}
        </div>
      )}

      {/* Findings */}
      {tab === "findings" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {org.findings.length === 0 ? (
            <div style={{ fontSize: 12, color: theme.color.faint }}>No open auditor findings.</div>
          ) : org.findings.map((f) => (
            <div key={f.id} style={{ background: FINDING_BG[f.status] ?? theme.color.bg, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "10px 12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: FINDING_COLOR[f.status] ?? theme.color.subtle }}>
                  {FINDING_LABEL[f.status] ?? f.status}
                </span>
                <span style={{ fontSize: 10, fontVariantNumeric: "tabular-nums", color: theme.color.faint }}>{f.controlId}</span>
                <span style={{ fontSize: 10, color: theme.color.faint, marginLeft: "auto" }}>{f.bureau} · {fmtDate(f.createdAt)}</span>
              </div>
              <div style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.5 }}>{f.text}</div>
            </div>
          ))}
        </div>
      )}

      {/* Schedule */}
      {tab === "schedule" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {org.upcomingEvents.length === 0 ? (
            <div style={{ fontSize: 12, color: theme.color.faint }}>No upcoming events scheduled.</div>
          ) : org.upcomingEvents.map((e, i) => (
            <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < org.upcomingEvents.length - 1 ? `1px solid ${theme.color.border}` : "none", fontSize: 12 }}>
              <span style={{
                fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
                color: EVENT_KIND_COLOR[e.kind] ?? theme.color.subtle,
                background: theme.color.bg, border: `1px solid ${theme.color.border}`,
                textTransform: "uppercase", letterSpacing: "0.05em", flexShrink: 0,
              }}>
                {e.kind}
              </span>
              <span style={{ flex: 1, color: theme.color.text, fontWeight: 600 }}>{e.title}</span>
              {e.frameworkId && <Pill label={FW_SHORT[e.frameworkId] ?? e.frameworkId} muted />}
              <span style={{ color: theme.color.faint, flexShrink: 0 }}>{fmtDate(e.eventDate)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Consultancy modals ────────────────────────────────────────────────────

function ConfirmNameInput({ expected, onConfirmed, submitting, onCancel }: {
  expected: string; onConfirmed: () => void; submitting: boolean; onCancel: () => void;
}) {
  const [value, setValue] = useState("");
  const matches = value.trim() === expected;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <input value={value} onChange={(e) => setValue(e.target.value)} placeholder={expected} autoFocus style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button onClick={onCancel} style={{ padding: "8px 18px", borderRadius: 3, fontSize: 13, background: "transparent", border: `1px solid ${theme.color.border}`, color: theme.color.muted, cursor: "pointer" }}>Cancel</button>
        <button disabled={!matches || submitting} onClick={onConfirmed} style={{ padding: "8px 18px", borderRadius: 3, fontSize: 13, fontWeight: 600, background: theme.color.danger, color: "#fff", border: "none", cursor: matches && !submitting ? "pointer" : "not-allowed", opacity: matches && !submitting ? 1 : 0.4 }}>
          {submitting ? "Removing…" : "Remove consultancy"}
        </button>
      </div>
    </div>
  );
}

function RemoveConsultancyModal({ consultancy, onClose, onRemoved }: { consultancy: ConsultancyDetail; onClose: () => void; onRemoved: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState("");

  async function handleRemove() {
    setRemoving(true); setError("");
    try {
      await api.del(`/admin/consultancies/${consultancy.id}`);
      onRemoved();
    } catch (err: unknown) {
      const e = err as { payload?: { error?: string }; message?: string };
      setError(apiErrorMessage(e, e?.message || "Failed to remove consultancy"));
      setRemoving(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...floatingSheet, padding: 20, width: "100%", maxWidth: 480 }}>
        {!confirming ? (
          <>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 10 }}>Remove consultancy</div>
            <div style={{ fontSize: 13, color: theme.color.subtle, marginBottom: 14, lineHeight: 1.6 }}>
              Removing <strong>{consultancy.name}</strong> will:
            </div>
            <ul style={{ fontSize: 13, color: theme.color.subtle, marginBottom: 20, paddingLeft: 20, lineHeight: 2 }}>
              <li>Release all <strong>{consultancy.organizations.length}</strong> client {consultancy.organizations.length === 1 ? "organisation" : "organisations"} from this firm</li>
              <li>Send each client org an email with a choice: transfer to another consultancy or delete their data</li>
              <li>Deactivate all <strong>{consultancy.users.length}</strong> consultant {consultancy.users.length === 1 ? "account" : "accounts"}</li>
              <li>Permanently delete the consultancy record</li>
            </ul>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 3, fontSize: 13, background: "transparent", border: `1px solid ${theme.color.border}`, color: theme.color.muted, cursor: "pointer" }}>Cancel</button>
              <button onClick={() => setConfirming(true)} style={{ padding: "8px 18px", borderRadius: 3, fontSize: 13, fontWeight: 600, background: "transparent", border: `1.5px solid ${theme.color.danger}`, color: theme.color.dangerText, cursor: "pointer" }}>Continue</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 10 }}>Are you sure?</div>
            <div style={{ fontSize: 13, color: theme.color.subtle, marginBottom: 20, lineHeight: 1.6 }}>
              Type <strong>{consultancy.name}</strong> to confirm removal. This action cannot be undone.
            </div>
            <ConfirmNameInput expected={consultancy.name} onConfirmed={handleRemove} submitting={removing} onCancel={onClose} />
            {error && <div style={{ fontSize: 12, color: theme.color.dangerText, marginTop: 10 }}>{error}</div>}
          </>
        )}
      </div>
    </div>
  );
}

function ConsultancyDetailModal({ id, onClose, onRemoved }: { id: string; onClose: () => void; onRemoved: () => void }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<ConsultancyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRemove, setShowRemove] = useState(false);

  // Client drill-down. "Client portal" is the routed read-through
  // (/consultant/clients/:orgId/portal): platform owners can scope any
  // organisation, so the page navigates there instead of embedding it.
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [orgDetail, setOrgDetail] = useState<AdminOrgDetail | null>(null);
  const [orgDetailLoading, setOrgDetailLoading] = useState(false);
  const [orgDetailTab, setOrgDetailTab] = useState<OrgDetailTab>("overview");
  const [viewMode, setViewMode] = useState<"admin" | "consultant">("admin");

  useEffect(() => {
    api.get<ConsultancyDetail>(`/admin/consultancies/${id}`)
      .then(setDetail).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  function openOrg(orgId: string) {
    setSelectedOrgId(orgId);
    setOrgDetail(null);
    setOrgDetailLoading(true);
    setOrgDetailTab("overview");
    setViewMode("admin");
    api.get<AdminOrgDetail>(`/admin/organizations/${orgId}`)
      .then(setOrgDetail).catch(() => {}).finally(() => setOrgDetailLoading(false));
  }

  function closeOrg() {
    setSelectedOrgId(null);
    setOrgDetail(null);
    setViewMode("admin");
  }

  const selectedOrg = detail?.organizations.find((o) => o.id === selectedOrgId);

  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
           onClick={(e) => { if (e.target === e.currentTarget && !selectedOrgId) onClose(); if (e.target === e.currentTarget && selectedOrgId) closeOrg(); }}>
        <div style={{ ...floatingSheet, padding: 20, width: "100%", maxWidth: 680, maxHeight: "82vh", display: "flex", flexDirection: "column" }}>

          {/* ── Client detail view ── */}
          {selectedOrgId ? (
            <>
              {/* Back header */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid var(--c-line)", flexShrink: 0 }}>
                <button
                  onClick={closeOrg}
                  style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.accentText, fontSize: 13, fontWeight: 600, padding: 0, display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}
                >
                  ← {detail?.name ?? "Back"}
                </button>
                <span style={{ color: theme.color.border }}>|</span>
                <div style={{ fontWeight: 700, color: theme.color.heading, fontSize: 14, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {selectedOrg?.name ?? "—"}
                </div>
                <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.muted, fontSize: 20, lineHeight: 1, flexShrink: 0 }}>×</button>
              </div>

              {/* View mode toggle: platform admin data, the consultant management view, or the routed client portal */}
              {detail?.platformOwner && (
                <div style={{ display: "flex", gap: 3, padding: 3, marginBottom: 14, background: theme.color.bg, borderRadius: 3, border: `1px solid ${theme.color.border}`, flexShrink: 0, alignSelf: "flex-start" }}>
                  {([["admin", "Platform admin"], ["consultant", "Consultant view"], ["portal", "Client portal"]] as const).map(([mode, label]) => (
                    <button
                      key={mode}
                      onClick={() => { if (mode === "portal") navigate(clientPortalPath(selectedOrgId)); else setViewMode(mode); }}
                      style={{
                        padding: "5px 13px", fontSize: 11, fontWeight: viewMode === mode ? 700 : 400, borderRadius: 3, border: "none", cursor: "pointer",
                        background: viewMode === mode ? theme.color.accentBg : "transparent",
                        color: viewMode === mode ? theme.color.accentText : theme.color.muted,
                        transition: "all 0.1s",
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}

              <div style={{ overflowY: "auto", flex: 1, display: "flex", flexDirection: "column" }}>
                {viewMode === "admin" && (
                  <>
                    {orgDetailLoading && <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading</div>}
                    {orgDetail && <AdminOrgDetailView org={orgDetail} tab={orgDetailTab} setTab={setOrgDetailTab} />}
                  </>
                )}
                {viewMode === "consultant" && selectedOrgId && (
                  <ConsultantClientView
                    orgId={selectedOrgId}
                    onBack={closeOrg}
                    onViewAsClient={() => navigate(clientPortalPath(selectedOrgId))}
                  />
                )}
              </div>
            </>
          ) : (
            /* ── Firm detail view ── */
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid var(--c-line)", flexShrink: 0 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading }}>{detail?.name ?? "Loading…"}</div>
                  {detail && <div style={{ fontSize: 11, color: theme.color.faint, fontFamily: "monospace", marginTop: 2 }}>/{detail.slug}</div>}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {detail && !detail.platformOwner && (
                    <button onClick={() => setShowRemove(true)} style={{ fontSize: 12, padding: "5px 12px", borderRadius: 3, border: `1.5px solid ${theme.color.danger}`, background: "transparent", color: theme.color.dangerText, cursor: "pointer", fontWeight: 600 }}>
                      Remove
                    </button>
                  )}
                  <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.muted, fontSize: 20, lineHeight: 1 }}>×</button>
                </div>
              </div>

              {loading && <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>}

              {detail && (
                <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 20, flex: 1 }}>
                  <div style={{ display: "flex", gap: 24, fontSize: 12, color: theme.color.subtle, flexWrap: "wrap" }}>
                    {detail.contactEmail && <span>Email: <strong style={{ color: theme.color.text }}>{detail.contactEmail}</strong></span>}
                    {detail.website && <span>Web: <a href={safeHref(detail.website)} target="_blank" rel="noopener noreferrer" style={{ color: theme.color.accentText }}>{detail.website}</a></span>}
                    <span>Joined: <strong style={{ color: theme.color.text }}>{fmtDate(detail.createdAt)}</strong></span>
                    {detail.platformOwner && <Pill label="Platform Owner" />}
                  </div>

                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                      Consultants ({detail.users.length})
                    </div>
                    {detail.users.length === 0 ? (
                      <div style={{ fontSize: 12, color: theme.color.faint }}>No consultants yet.</div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {detail.users.map((u) => (
                          <div key={u.id} style={{ fontSize: 12, display: "flex", gap: 12, alignItems: "center" }}>
                            <span style={{ fontWeight: 600, minWidth: 160, color: theme.color.text }}>{u.displayName}</span>
                            <span style={{ color: theme.color.subtle, flex: 1 }}>{u.email}</span>
                            <ActiveBadge active={u.isActive} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                      Client organisations ({detail.organizations.length}) — click to drill in
                    </div>
                    {detail.organizations.length === 0 ? (
                      <div style={{ fontSize: 12, color: theme.color.faint }}>No clients onboarded yet.</div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {detail.organizations.map((o) => (
                          <div
                            key={o.id}
                            onClick={() => openOrg(o.id)}
                            style={{
                              display: "flex", alignItems: "center", gap: 10, padding: "9px 10px",
                              borderRadius: 3, cursor: "pointer", border: `1px solid ${theme.color.border}`,
                              background: theme.color.bg,
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = theme.color.panel; e.currentTarget.style.borderColor = theme.color.accent + "55"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = theme.color.bg; e.currentTarget.style.borderColor = theme.color.border; }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: 12, color: theme.color.heading }}>{o.name}</div>
                              {o.sector && <div style={{ fontSize: 10, color: theme.color.faint, marginTop: 1 }}>{o.sector}{o.size ? ` · ${o.size}` : ""}</div>}
                            </div>
                            <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                              {o.frameworks.slice(0, 3).map((f) => <Pill key={f} label={FW_SHORT[f] ?? f} muted />)}
                            </div>
                            <span style={{ fontSize: 11, color: theme.color.faint, minWidth: 70, flexShrink: 0 }}>{o.serviceTier.name}</span>
                            <span style={{ fontWeight: 700, fontSize: 13, color: scoreColor(o.latestScore), minWidth: 36, textAlign: "right", flexShrink: 0 }}>
                              {o.latestScore !== null ? `${o.latestScore}%` : "—"}
                            </span>
                            <span style={{ color: theme.color.faint, fontSize: 14, flexShrink: 0 }}>›</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {showRemove && detail && (
        <RemoveConsultancyModal
          consultancy={detail}
          onClose={() => setShowRemove(false)}
          onRemoved={() => { setShowRemove(false); onClose(); onRemoved(); }}
        />
      )}
    </>
  );
}

function InviteConsultancyModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [firmName, setFirmName] = useState("");
  const [slug, setSlug] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  // Sign-in policy (backend/src/lib/signInPolicy.ts); the firm's lead consultant decides once they join.
  const [ssoRequired, setSsoRequired] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  function autoSlug(name: string) {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  }
  function handleNameChange(v: string) {
    setFirmName(v);
    if (!slugTouched) setSlug(autoSlug(v));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault(); setError(""); setSubmitting(true);
    try {
      await api.post("/invites/consultancy", { firmName, slug, contactEmail, ssoRequired });
      setDone(true); onDone();
    } catch (err: unknown) {
      const e = err as { payload?: { error?: string }; message?: string };
      setError(apiErrorMessage(e, e?.message || "Failed to send invite"));
    } finally { setSubmitting(false); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...floatingSheet, padding: 20, width: "100%", maxWidth: 480 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, paddingBottom: 12, borderBottom: "1px solid var(--c-line)" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading }}>Invite New Consultancy</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.muted, fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        {done ? (
          <div style={{ background: cbSafe("#052e16"), border: cbSafe("1px solid #6ee7b7"), borderRadius: 3, padding: "12px 16px", fontSize: 13, color: theme.color.successText }}>
            Invite sent to <strong>{contactEmail}</strong>. They'll receive a link to claim their firm account.
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 11, color: theme.color.subtle, fontWeight: 600 }}>Firm name</label>
              <input style={{ ...inputStyle }} value={firmName} onChange={(e) => handleNameChange(e.target.value)} autoFocus required placeholder="Acme Security Ltd" />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 11, color: theme.color.subtle, fontWeight: 600 }}>URL slug</label>
              <input style={{ ...inputStyle, fontFamily: "monospace" }} value={slug} onChange={(e) => { setSlug(e.target.value); setSlugTouched(true); }} required pattern="^[a-z0-9-]+$" placeholder="acme-security" />
              <div style={{ fontSize: 11, color: theme.color.faint }}>Lowercase letters, numbers, hyphens only.</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 11, color: theme.color.subtle, fontWeight: 600 }}>Contact email</label>
              <input style={{ ...inputStyle }} type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} required placeholder="admin@acmesecurity.co.uk" />
            </div>
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: theme.color.text, cursor: "pointer" }}>
              <input type="checkbox" checked={ssoRequired} onChange={(e) => setSsoRequired(e.target.checked)} style={{ marginTop: 2 }} />
              <span>
                <strong>Require single sign-on</strong> (recommended): staff sign in with their Microsoft 365 or Google Workspace
                work account. Untick only if the firm has neither.
              </span>
            </label>
            {error && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
              <button type="button" onClick={onClose} style={{ ...inputStyle, width: "auto", padding: "8px 16px", cursor: "pointer" }}>Cancel</button>
              <button type="submit" disabled={submitting || !firmName.trim() || !slug.trim() || !contactEmail.trim()} style={{ ...btnPrimary, opacity: submitting ? 0.7 : 1 }}>
                {submitting ? "Sending…" : "Send invite"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Tab: Overview ─────────────────────────────────────────────────────────

function OverviewTab({ stats, onNavigate }: { stats: PlatformStats; onNavigate: (tab: AdminTab) => void }) {
  const hasAttention = stats.attentionItems.length > 0;
  const totalAssessed = stats.compliantClients + stats.partialClients + stats.atRiskClients;

  function relTime(iso: string | null) {
    if (!iso) return "";
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 2) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Attention panel */}
      {hasAttention && (
        <div style={{ background: "rgba(180,100,0,0.08)", border: `1px solid rgba(180,100,0,0.3)`, borderRadius: 3, padding: "14px 18px" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.warningText, marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
            <AlertTriangle size={13} style={{ flexShrink: 0 }} />
            <span>{stats.attentionItems.length} item{stats.attentionItems.length !== 1 ? "s" : ""} need attention</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {stats.attentionItems.map((item) => (
              <div key={item.org.id} style={{ background: theme.color.bg, borderRadius: 3, border: `1px solid ${theme.color.border}`, padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "0.07em", color: item.type === "critical_score" ? theme.color.dangerText : theme.color.warningText, marginBottom: 4 }}>
                    {item.type === "critical_score" ? "Critical Score" : "Never Assessed"}
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: theme.color.heading, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>
                    {item.org.name}
                    {item.type === "critical_score" && item.org.score !== null && (
                      <span style={{ marginLeft: 8, color: theme.color.dangerText, fontWeight: 700 }}>{item.org.score}%</span>
                    )}
                  </div>
                  {item.org.consultancyName && (
                    <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 2 }}>
                      {item.org.consultancyName}{item.org.frameworkId ? ` · ${FW_SHORT[item.org.frameworkId] ?? item.org.frameworkId}` : ""}
                    </div>
                  )}
                  {item.type === "never_assessed" && (
                    <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 2 }}>
                      Enrolled{item.org.frameworkId ? ` in ${FW_SHORT[item.org.frameworkId] ?? item.org.frameworkId}` : ""} — no AI check run yet
                    </div>
                  )}
                </div>
                <button
                  onClick={() => onNavigate("clients")}
                  style={{ fontSize: 11, fontWeight: 600, padding: "5px 12px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.muted, cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" as const }}
                >
                  View clients →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!hasAttention && (
        <div style={{ background: cbSafe("rgba(22,163,74,0.07)"), border: cbSafe("1px solid rgba(22,163,74,0.25)"), borderRadius: 3, padding: "14px 18px", fontSize: 13, color: theme.color.successText, fontWeight: 600 }}>
          ✓ No items need attention
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "start" }}>
        {/* Compliance health */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.subtle, textTransform: "uppercase" as const, letterSpacing: "0.06em", marginBottom: 10 }}>
            Compliance Health — All {stats.clients} Clients
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {[
              { label: "Compliant", count: stats.compliantClients, color: theme.color.successText, bg: cbSafe("rgba(22,163,74,0.08)"), border: cbSafe("rgba(22,163,74,0.25)") },
              { label: "Partial", count: stats.partialClients, color: theme.color.warningText, bg: "rgba(180,100,0,0.08)", border: "rgba(180,100,0,0.25)" },
              { label: "At Risk", count: stats.atRiskClients, color: theme.color.dangerText, bg: cbSafe("rgba(220,38,38,0.08)"), border: cbSafe("rgba(220,38,38,0.25)") },
              { label: "Not Assessed", count: stats.neverAssessedClients, color: theme.color.faint, bg: theme.color.bg, border: theme.color.border },
            ].map(({ label, count, color, bg, border }) => (
              <div
                key={label}
                onClick={() => onNavigate("clients")}
                style={{ background: bg, border: `1px solid ${border}`, borderRadius: 3, padding: "14px 16px", textAlign: "center", cursor: "pointer" }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{count}</div>
                <div style={{ fontSize: 10, color, marginTop: 5, fontWeight: 600 }}>{label}</div>
              </div>
            ))}
          </div>
          {totalAssessed > 0 && (
            <div style={{ marginTop: 10, height: 6, borderRadius: 4, background: theme.color.border, overflow: "hidden", display: "flex" }}>
              {stats.compliantClients > 0 && <div style={{ width: `${(stats.compliantClients / stats.clients) * 100}%`, background: theme.color.successText }} />}
              {stats.partialClients > 0 && <div style={{ width: `${(stats.partialClients / stats.clients) * 100}%`, background: theme.color.warningText }} />}
              {stats.atRiskClients > 0 && <div style={{ width: `${(stats.atRiskClients / stats.clients) * 100}%`, background: theme.color.dangerText }} />}
            </div>
          )}
        </div>

        {/* Recent activity */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.subtle, textTransform: "uppercase" as const, letterSpacing: "0.06em", marginBottom: 10 }}>
            Recent Platform Activity
          </div>
          {stats.recentActivity.length === 0 ? (
            <div style={{ fontSize: 12, color: theme.color.faint }}>No AI checks completed yet.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {stats.recentActivity.map((r) => (
                <div key={r.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 12 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: scoreColor(r.score), flexShrink: 0, marginTop: 4 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: theme.color.text, fontWeight: 600 }}>
                      AI check completed — <span style={{ fontWeight: 400 }}>{r.orgName}</span>
                    </div>
                    <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 1 }}>
                      {r.score !== null ? `Score ${r.score}%` : "Score pending"}{r.completedAt ? ` · ${relTime(r.completedAt)}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {([
          ["consultancies", "View Consultancies →"],
          ["clients", "View Clients →"],
          ["revenue", "View Revenue →"],
        ] as const).map(([tab, label]) => (
          <button
            key={tab}
            onClick={() => onNavigate(tab)}
            style={{ fontSize: 12, fontWeight: 600, padding: "7px 16px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: theme.color.bg, color: theme.color.muted, cursor: "pointer" }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = theme.color.accent + "80"; e.currentTarget.style.color = theme.color.accentText; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = theme.color.border; e.currentTarget.style.color = theme.color.muted; }}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Tab: Consultancies ────────────────────────────────────────────────────

function ConsultanciesTab({ onRefreshStats }: { onRefreshStats: () => void }) {
  const [consultancies, setConsultancies] = useState<ConsultancySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  function load() {
    setLoading(true);
    api.get<{ consultancies: ConsultancySummary[] }>("/admin/consultancies")
      .then((d) => setConsultancies(d.consultancies))
      .catch(() => {})
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  const filtered = consultancies.filter((c) =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.slug.includes(search.toLowerCase())
  );

  // The Jersey edition seat licence (PATCH /admin/consultancies/:id/jersey-licence). The platform
  // owner sells the edition directly and needs none.
  const [licenceBusy, setLicenceBusy] = useState<string | null>(null);
  async function toggleJerseyLicence(c: ConsultancySummary) {
    setLicenceBusy(c.id);
    try {
      await api.patch(`/admin/consultancies/${c.id}/jersey-licence`, { licensed: !c.jerseyEditionLicensed });
      load();
    } catch { /* the list reloads unchanged */ }
    finally { setLicenceBusy(null); }
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search firms…" />
        <button onClick={() => setShowInvite(true)} style={{ ...btnPrimary }}>+ Invite Consultancy</button>
      </div>

      {loading && <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>}

      {!loading && (
        <div style={{ ...card, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--c-line)" }}>
                {["Firm", "Slug", "Clients", "Consultants", "Jersey edition", "Joined", ""].map((h) => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <EmptyRow cols={7} message={search ? "No firms match your search." : "No consultancies yet."} />
              ) : filtered.map((c) => (
                <tr key={c.id} style={{ borderBottom: `1px solid ${theme.color.border}` }}>
                  <td style={td}>
                    <div style={{ fontWeight: 600, color: theme.color.heading }}>{c.name}</div>
                    {c.platformOwner && <Pill label="Platform Owner" />}
                  </td>
                  <td style={{ ...td, fontFamily: "monospace", fontSize: 11, color: theme.color.subtle }}>{c.slug}</td>
                  <td style={{ ...td, color: theme.color.text }}>{c._count.organizations}</td>
                  <td style={{ ...td, color: theme.color.text }}>{c._count.users}</td>
                  <td style={td}>
                    {c.platformOwner ? (
                      <span style={{ fontSize: 11, color: theme.color.subtle }}>Sells directly</span>
                    ) : (
                      <button
                        onClick={() => toggleJerseyLicence(c)}
                        disabled={licenceBusy === c.id}
                        title={`Billed ${gbp(JERSEY_SEAT_LICENCE_PENCE)} per active consultant seat per month`}
                        style={{
                          fontSize: 11, padding: "4px 10px", borderRadius: 4, cursor: "pointer",
                          border: `1px solid ${c.jerseyEditionLicensed ? theme.color.accent : theme.color.border}`,
                          background: c.jerseyEditionLicensed ? theme.color.accentBg : "transparent",
                          color: c.jerseyEditionLicensed ? theme.color.accentText : theme.color.muted,
                          opacity: licenceBusy === c.id ? 0.6 : 1,
                        }}
                      >
                        {c.jerseyEditionLicensed ? "Licensed — withdraw" : "Grant licence"}
                      </button>
                    )}
                  </td>
                  <td style={{ ...td, color: theme.color.faint }}>{fmtDate(c.createdAt)}</td>
                  <td style={td}>
                    <button onClick={() => setSelectedId(c.id)} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 4, border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.muted, cursor: "pointer" }}>
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showInvite && <InviteConsultancyModal onClose={() => setShowInvite(false)} onDone={() => { load(); onRefreshStats(); }} />}
      {selectedId && (
        <ConsultancyDetailModal id={selectedId} onClose={() => setSelectedId(null)} onRemoved={() => { setSelectedId(null); load(); onRefreshStats(); }} />
      )}
    </div>
  );
}

// ── Tab: Clients ──────────────────────────────────────────────────────────

function OrgDetailPanel({ org, consultancies, onClose, onReassigned }: {
  org: OrgSummary;
  consultancies: { id: string; name: string }[];
  onClose: () => void;
  onReassigned: (orgId: string, consultancy: { id: string; name: string } | null) => void;
}) {
  const [selectedConsultancyId, setSelectedConsultancyId] = useState(org.consultancy?.id ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isDirty = selectedConsultancyId !== (org.consultancy?.id ?? "");

  async function handleReassign() {
    if (!selectedConsultancyId) return;
    setSaving(true); setError("");
    try {
      await api.patch(`/admin/organizations/${org.id}`, { consultancyId: selectedConsultancyId });
      const c = consultancies.find((c) => c.id === selectedConsultancyId) ?? null;
      onReassigned(org.id, c);
      onClose();
    } catch (err: unknown) {
      const e = err as { payload?: { error?: string }; message?: string };
      setError(apiErrorMessage(e, e?.message || "Failed to reassign"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1500 }} />
      <div style={{
        ...floatingSheet,
        position: "fixed", top: 0, right: 0, bottom: 0, width: 380,
        zIndex: 1600, display: "flex", flexDirection: "column", padding: 28, gap: 20, overflowY: "auto",
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, paddingBottom: 12, borderBottom: "1px solid var(--c-line)" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: theme.color.heading }}>{org.name}</div>
            {org.sector && <div style={{ fontSize: 12, color: theme.color.faint, marginTop: 2 }}>{org.sector}</div>}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.faint, fontSize: 20, lineHeight: 1, padding: 0 }}>×</button>
        </div>

        {org.releasedAt && (
          <div style={{ fontSize: 12, padding: "8px 12px", borderRadius: 3, background: "#451a03", color: theme.color.warningText, border: `1px solid ${theme.color.border}` }}>
            Released — awaiting client decision since {fmtDate(org.releasedAt)}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[
            ["Tier", org.serviceTier.name],
            ["Users", String(org.userCount)],
            ["Joined", fmtDate(org.createdAt)],
            ["Score", org.latestScore !== null ? `${org.latestScore}%` : "No data"],
          ].map(([label, value]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, borderBottom: `1px solid ${theme.color.border}`, paddingBottom: 8 }}>
              <span style={{ color: theme.color.subtle }}>{label}</span>
              <span style={{ color: theme.color.text, fontWeight: 500 }}>{value}</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", fontSize: 13, borderBottom: `1px solid ${theme.color.border}`, paddingBottom: 8 }}>
            <span style={{ color: theme.color.subtle }}>Frameworks</span>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {org.frameworks.length === 0
                ? <span style={{ color: theme.color.faint }}>None</span>
                : org.frameworks.map((f) => <Pill key={f} label={FW_SHORT[f] ?? f} muted />)
              }
            </div>
          </div>
        </div>

        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: theme.color.subtle, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Reassign to Consultancy
          </label>
          <select
            value={selectedConsultancyId}
            onChange={(e) => setSelectedConsultancyId(e.target.value)}
            style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}
          >
            <option value="">— Unassigned (Direct) —</option>
            {consultancies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {error && <div style={{ fontSize: 12, color: theme.color.dangerText, marginTop: 6 }}>{error}</div>}
          <button
            disabled={!isDirty || saving || !selectedConsultancyId}
            onClick={handleReassign}
            style={{ ...btnPrimary, marginTop: 12, width: "100%", opacity: (!isDirty || !selectedConsultancyId) ? 0.4 : 1 }}
          >
            {saving ? "Saving…" : "Reassign"}
          </button>
        </div>
      </div>
    </>
  );
}

function ClientsTab() {
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedOrg, setSelectedOrg] = useState<OrgSummary | null>(null);
  const [consultancies, setConsultancies] = useState<{ id: string; name: string }[]>([]);

  function load() {
    setLoading(true);
    Promise.all([
      api.get<{ organizations: OrgSummary[] }>("/admin/organizations"),
      api.get<{ consultancies: { id: string; name: string; platformOwner: boolean }[] }>("/admin/consultancies"),
    ])
      .then(([orgRes, cRes]) => {
        setOrgs(orgRes.organizations);
        setConsultancies(cRes.consultancies.filter((c) => !c.platformOwner));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  function handleReassigned(orgId: string, consultancy: { id: string; name: string } | null) {
    setOrgs((prev) => prev.map((o) => o.id === orgId ? { ...o, consultancy, releasedAt: null } : o));
  }

  const filtered = orgs.filter((o) =>
    !search ||
    o.name.toLowerCase().includes(search.toLowerCase()) ||
    (o.consultancy?.name ?? "").toLowerCase().includes(search.toLowerCase()) ||
    (o.sector ?? "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search organisations…" />
      </div>
      {loading && <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>}
      {!loading && (
        <div style={{ ...card, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--c-line)" }}>
                {["Organisation", "Consultancy", "Tier", "Frameworks", "Score", "Users", "Joined"].map((h) => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <EmptyRow cols={7} message={search ? "No organisations match your search." : "No client organisations yet."} />
              ) : filtered.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => setSelectedOrg(o)}
                  style={{ borderBottom: `1px solid ${theme.color.border}`, cursor: "pointer", background: selectedOrg?.id === o.id ? theme.color.panel : undefined }}
                >
                  <td style={td}>
                    <div style={{ fontWeight: 600, color: theme.color.heading }}>{o.name}</div>
                    {o.sector && <div style={{ fontSize: 11, color: theme.color.faint }}>{o.sector}</div>}
                    {o.releasedAt && <div style={{ fontSize: 10, color: theme.color.warningText, marginTop: 2 }}>Released</div>}
                  </td>
                  <td style={{ ...td, color: o.consultancy ? theme.color.text : theme.color.faint }}>
                    {o.consultancy?.name ?? "Direct"}
                  </td>
                  <td style={{ ...td, color: theme.color.subtle }}>{o.serviceTier.name}</td>
                  <td style={td}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {o.frameworks.length === 0 ? (
                        <span style={{ fontSize: 11, color: theme.color.faint }}>None</span>
                      ) : o.frameworks.map((f) => (
                        <Pill key={f} label={FW_SHORT[f] ?? f} muted />
                      ))}
                    </div>
                  </td>
                  <td style={{ ...td, fontWeight: 700, color: scoreColor(o.latestScore) }}>
                    {o.latestScore !== null ? `${o.latestScore}%` : "—"}
                  </td>
                  <td style={{ ...td, color: theme.color.text }}>{o.userCount}</td>
                  <td style={{ ...td, color: theme.color.faint }}>{fmtDate(o.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selectedOrg && (
        <OrgDetailPanel
          org={selectedOrg}
          consultancies={consultancies}
          onClose={() => setSelectedOrg(null)}
          onReassigned={handleReassigned}
        />
      )}
    </div>
  );
}

// ── Tab: Consultants ──────────────────────────────────────────────────────

function ConsultantsTab() {
  const [consultants, setConsultants] = useState<ConsultantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [toggling, setToggling] = useState<Set<string>>(new Set());

  useEffect(() => {
    api.get<{ consultants: ConsultantUser[] }>("/admin/consultants")
      .then((d) => setConsultants(d.consultants))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function toggleUser(userId: string) {
    setToggling((prev) => new Set(prev).add(userId));
    try {
      const res = await api.patch<{ user: { id: string; isActive: boolean } }>(`/admin/users/${userId}`, {});
      setConsultants((prev) => prev.map((c) => c.id === userId ? { ...c, isActive: res.user.isActive } : c));
    } catch { /* error ignored — badge stays unchanged */ }
    finally { setToggling((prev) => { const n = new Set(prev); n.delete(userId); return n; }); }
  }

  const filtered = consultants.filter((c) =>
    !search ||
    c.displayName.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase()) ||
    (c.consultancy?.name ?? "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <SearchInput value={search} onChange={setSearch} placeholder="Search consultants…" />
      {loading && <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>}
      {!loading && (
        <div style={{ ...card, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--c-line)" }}>
                {["Name", "Email", "Firm", "Role", "Status", "Joined", ""].map((h) => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <EmptyRow cols={7} message={search ? "No consultants match your search." : "No consultants yet."} />
              ) : filtered.map((c) => {
                const isPlatformOwner = !!c.consultancy?.platformOwner;
                return (
                  <tr key={c.id} style={{ borderBottom: `1px solid ${theme.color.border}` }}>
                    <td style={{ ...td, fontWeight: 600, color: theme.color.heading }}>{c.displayName}</td>
                    <td style={{ ...td, color: theme.color.subtle }}>{c.email}</td>
                    <td style={{ ...td, color: theme.color.text }}>{c.consultancy?.name ?? "—"}</td>
                    <td style={td}>
                      {isPlatformOwner ? <Pill label="Platform Owner" /> : <span style={{ fontSize: 12, color: theme.color.subtle }}>Consultant</span>}
                    </td>
                    <td style={td}><ActiveBadge active={c.isActive} /></td>
                    <td style={{ ...td, color: theme.color.faint }}>{fmtDate(c.createdAt)}</td>
                    <td style={{ ...td, textAlign: "right" }}>
                      {!isPlatformOwner && (
                        <button
                          disabled={toggling.has(c.id)}
                          onClick={() => toggleUser(c.id)}
                          style={{
                            fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 3, cursor: "pointer",
                            background: "transparent", border: `1px solid ${theme.color.border}`,
                            color: c.isActive ? theme.color.dangerText : theme.color.successText,
                            opacity: toggling.has(c.id) ? 0.5 : 1,
                          }}
                        >
                          {toggling.has(c.id) ? "…" : c.isActive ? "Disable" : "Enable"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Tab: Auditors ─────────────────────────────────────────────────────────

const EMPTY_BUREAU_FORM = { name: "", accreditation: "", country: "", certFeePence: "", annualFeePence: "" };

function AuditorsTab() {
  const [bureaux, setBureaux] = useState<Bureau[]>([]);
  const [auditors, setAuditors] = useState<AuditorUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [toggling, setToggling] = useState<Set<string>>(new Set());
  const [showAddBureau, setShowAddBureau] = useState(false);
  const [bureauForm, setBureauForm] = useState(EMPTY_BUREAU_FORM);
  const [addingBureau, setAddingBureau] = useState(false);
  const [bureauError, setBureauError] = useState("");

  useEffect(() => {
    api.get<{ bureaux: Bureau[]; auditors: AuditorUser[] }>("/admin/auditors")
      .then((d) => { setBureaux(d.bureaux); setAuditors(d.auditors); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function toggleUser(userId: string) {
    setToggling((prev) => new Set(prev).add(userId));
    try {
      const res = await api.patch<{ user: { id: string; isActive: boolean } }>(`/admin/users/${userId}`, {});
      setAuditors((prev) => prev.map((a) => a.id === userId ? { ...a, isActive: res.user.isActive } : a));
    } catch { /* error ignored — badge stays unchanged */ }
    finally { setToggling((prev) => { const n = new Set(prev); n.delete(userId); return n; }); }
  }

  async function handleAddBureau(e: React.FormEvent) {
    e.preventDefault();
    if (!bureauForm.name.trim()) { setBureauError("Bureau name is required"); return; }
    setAddingBureau(true); setBureauError("");
    try {
      const res = await api.post<{ bureau: Bureau }>("/admin/bureaux", {
        name: bureauForm.name.trim(),
        accreditation: bureauForm.accreditation.trim() || undefined,
        country: bureauForm.country.trim() || undefined,
        certFeePence: bureauForm.certFeePence ? Number(bureauForm.certFeePence) : undefined,
        annualFeePence: bureauForm.annualFeePence ? Number(bureauForm.annualFeePence) : undefined,
      });
      setBureaux((prev) => [...prev, res.bureau].sort((a, b) => a.name.localeCompare(b.name)));
      setBureauForm(EMPTY_BUREAU_FORM);
      setShowAddBureau(false);
    } catch (err: unknown) {
      const e = err as { payload?: { error?: string }; message?: string };
      setBureauError(apiErrorMessage(e, e?.message || "Failed to create bureau"));
    } finally {
      setAddingBureau(false);
    }
  }

  const filteredAuditors = auditors.filter((a) =>
    !search ||
    a.displayName.toLowerCase().includes(search.toLowerCase()) ||
    a.email.toLowerCase().includes(search.toLowerCase()) ||
    (a.bureau?.name ?? "").toLowerCase().includes(search.toLowerCase())
  );
  const filteredBureaux = bureaux.filter((b) =>
    !search || b.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
      <SearchInput value={search} onChange={setSearch} placeholder="Search auditors or bureaux…" />
      {loading && <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>}

      {!loading && (
        <>
          {/* Bureaux */}
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <DimensionHeading as="h3" count={filteredBureaux.length} style={{ flex: 1, margin: "0 12px 0 0" }}>
                Certification Bureaux
              </DimensionHeading>
              <button
                onClick={() => { setShowAddBureau((v) => !v); setBureauError(""); setBureauForm(EMPTY_BUREAU_FORM); }}
                style={{ ...btnPrimary, fontSize: 12, padding: "5px 14px" }}
              >
                {showAddBureau ? "Cancel" : "+ Add Bureau"}
              </button>
            </div>

            {showAddBureau && (
              <form onSubmit={handleAddBureau} style={{ ...card, marginBottom: 12, display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>New Certification Bureau</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 11, color: theme.color.subtle, display: "block", marginBottom: 4 }}>Name *</label>
                    <input value={bureauForm.name} onChange={(e) => setBureauForm((f) => ({ ...f, name: e.target.value }))} style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} placeholder="e.g. BSI Group" />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: theme.color.subtle, display: "block", marginBottom: 4 }}>Accreditation</label>
                    <input value={bureauForm.accreditation} onChange={(e) => setBureauForm((f) => ({ ...f, accreditation: e.target.value }))} style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} placeholder="e.g. UKAS" />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: theme.color.subtle, display: "block", marginBottom: 4 }}>Country</label>
                    <input value={bureauForm.country} onChange={(e) => setBureauForm((f) => ({ ...f, country: e.target.value }))} style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} placeholder="e.g. GB" />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: theme.color.subtle, display: "block", marginBottom: 4 }}>Cert Fee (pence)</label>
                    <input type="number" min="0" value={bureauForm.certFeePence} onChange={(e) => setBureauForm((f) => ({ ...f, certFeePence: e.target.value }))} style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} placeholder="e.g. 150000" />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: theme.color.subtle, display: "block", marginBottom: 4 }}>Annual Fee (pence)</label>
                    <input type="number" min="0" value={bureauForm.annualFeePence} onChange={(e) => setBureauForm((f) => ({ ...f, annualFeePence: e.target.value }))} style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} placeholder="e.g. 75000" />
                  </div>
                </div>
                {bureauError && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{bureauError}</div>}
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button type="submit" disabled={addingBureau} style={{ ...btnPrimary, opacity: addingBureau ? 0.6 : 1 }}>
                    {addingBureau ? "Creating…" : "Create Bureau"}
                  </button>
                </div>
              </form>
            )}

            <div style={{ ...card, overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--c-line)" }}>
                    {["Bureau", "Accreditation", "Country", "Client Orgs", "Auditors"].map((h) => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredBureaux.length === 0 ? (
                    <EmptyRow cols={5} message="No bureaux found." />
                  ) : filteredBureaux.map((b) => (
                    <tr key={b.id} style={{ borderBottom: `1px solid ${theme.color.border}` }}>
                      <td style={{ ...td, fontWeight: 600, color: theme.color.heading }}>{b.name}</td>
                      <td style={{ ...td, color: theme.color.subtle }}>{b.accreditation ?? "—"}</td>
                      <td style={{ ...td, color: theme.color.subtle }}>{b.country ?? "—"}</td>
                      <td style={{ ...td, color: theme.color.text }}>{b._count.organizations}</td>
                      <td style={{ ...td, color: theme.color.text }}>{b._count.users}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Auditor users */}
          <div>
            <DimensionHeading as="h3" count={filteredAuditors.length}>
              Auditor Accounts
            </DimensionHeading>
            <div style={{ ...card, overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--c-line)" }}>
                    {["Name", "Email", "Bureau", "Status", "Joined", ""].map((h) => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredAuditors.length === 0 ? (
                    <EmptyRow cols={6} message="No auditors found." />
                  ) : filteredAuditors.map((a) => (
                    <tr key={a.id} style={{ borderBottom: `1px solid ${theme.color.border}` }}>
                      <td style={{ ...td, fontWeight: 600, color: theme.color.heading }}>{a.displayName}</td>
                      <td style={{ ...td, color: theme.color.subtle }}>{a.email}</td>
                      <td style={{ ...td, color: theme.color.text }}>{a.bureau?.name ?? "—"}</td>
                      <td style={td}><ActiveBadge active={a.isActive} /></td>
                      <td style={{ ...td, color: theme.color.faint }}>{fmtDate(a.createdAt)}</td>
                      <td style={{ ...td, textAlign: "right" }}>
                        <button
                          disabled={toggling.has(a.id)}
                          onClick={() => toggleUser(a.id)}
                          style={{
                            fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 3, cursor: "pointer",
                            background: "transparent", border: `1px solid ${theme.color.border}`,
                            color: a.isActive ? theme.color.dangerText : theme.color.successText,
                            opacity: toggling.has(a.id) ? 0.5 : 1,
                          }}
                        >
                          {toggling.has(a.id) ? "…" : a.isActive ? "Disable" : "Enable"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Tab: Revenue ──────────────────────────────────────────────────────────

interface RevenueClient {
  id: string;
  name: string;
  tier: string;
  frameworks: string[];
  abacasRevenuePence: number;
  consultancyRevenuePence: number;
  totalBilledPence: number;
}

interface RevenueConsultancy {
  id: string;
  name: string;
  clientCount: number;
  abacasRevenuePence: number;
  consultancyRevenuePence: number;
  totalBilledPence: number;
  clients: RevenueClient[];
}

interface RevenueData {
  periodMonth: number;
  periodYear: number;
  // Stream 1: Abacas earns as platform owner (from ALL clients across the platform)
  platformRevenuePence: number;
  // Stream 2: Abacas earns as a consultancy (time billing from their own direct clients)
  abacasConsultancyRevenuePence: number;
  abacasClientCount: number;
  abacasClients: RevenueClient[];
  // What third-party consultancies earn in time billing
  thirdPartyConsultancyRevenuePence: number;
  consultancies: RevenueConsultancy[];
}

function fmtGbp(pence: number) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(pence / 100);
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function RevenueTab() {
  const now = new Date();
  const [periodMonth, setPeriodMonth] = useState(now.getMonth() + 1);
  const [periodYear,  setPeriodYear]  = useState(now.getFullYear());
  const [data, setData] = useState<RevenueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showAbacasClients, setShowAbacasClients] = useState(false);

  useEffect(() => {
    setLoading(true); setData(null);
    api.get<RevenueData>("/admin/revenue", { month: String(periodMonth), year: String(periodYear) })
      .then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [periodMonth, periodYear]);

  function stepMonth(delta: number) {
    setPeriodMonth((m) => {
      let nm = m + delta;
      let ny = periodYear;
      if (nm > 12) { nm = 1; ny++; }
      if (nm < 1)  { nm = 12; ny--; }
      setPeriodYear(ny);
      return nm;
    });
    setExpanded(new Set());
    setShowAbacasClients(false);
  }

  const isCurrentMonth = periodMonth === now.getMonth() + 1 && periodYear === now.getFullYear();

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  const period = `${MONTH_NAMES[periodMonth - 1]} ${periodYear}`;
  const periodNav = (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
      <button onClick={() => stepMonth(-1)} style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "4px 12px", fontSize: 13, color: theme.color.muted, cursor: "pointer" }}>←</button>
      <span style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, minWidth: 110, textAlign: "center" }}>{period}</span>
      <button onClick={() => stepMonth(1)} disabled={isCurrentMonth} style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "4px 12px", fontSize: 13, color: isCurrentMonth ? theme.color.faint : theme.color.muted, cursor: isCurrentMonth ? "not-allowed" : "pointer", opacity: isCurrentMonth ? 0.4 : 1 }}>→</button>
    </div>
  );

  if (loading) return <div>{periodNav}<div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div></div>;
  if (!data) return <div>{periodNav}<div style={{ color: theme.color.dangerText, fontSize: 13 }}>Failed to load revenue data.</div></div>;

  const colHead: React.CSSProperties = {
    padding: "8px 12px", textAlign: "right", fontSize: 10,
    color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600,
  };
  const colLeft: React.CSSProperties = { ...colHead, textAlign: "left" };
  const colCell: React.CSSProperties = { padding: "10px 12px", textAlign: "right", fontSize: 13 };

  const sectionLabel: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, color: theme.color.subtle,
    textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
      {periodNav}

      {/* ── Stream 1: Abacas platform revenue ── */}
      <div>
        <div style={sectionLabel}>Abacas Platform Revenue — {period}</div>
        <div style={{ fontSize: 12, color: theme.color.faint, marginBottom: 12 }}>
          SaaS fees Abacas earns from every client on the platform, regardless of consultancy.
        </div>
        <div style={{ ...card, padding: "20px 24px", display: "inline-flex", gap: 32, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, color: theme.color.faint, marginBottom: 2 }}>Platform revenue</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: theme.color.successText }}>{fmtGbp(data.platformRevenuePence)}</div>
          </div>
        </div>
      </div>

      {/* ── Stream 2: Abacas consultancy revenue ── */}
      <div>
        <div style={sectionLabel}>Abacas Consultancy Revenue — {period}</div>
        <div style={{ fontSize: 12, color: theme.color.faint, marginBottom: 12 }}>
          Time billing from Abacas's own direct clients ({data.abacasClientCount} client{data.abacasClientCount !== 1 ? "s" : ""}).
        </div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 12 }}>
          <div style={{ ...card, padding: "16px 20px", minWidth: 160 }}>
            <div style={{ fontSize: 11, color: theme.color.faint, marginBottom: 2 }}>Consultancy revenue</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: theme.color.consultant }}>{fmtGbp(data.abacasConsultancyRevenuePence)}</div>
          </div>
        </div>
        {data.abacasClientCount > 0 && (
          <button
            onClick={() => setShowAbacasClients((v) => !v)}
            style={{ fontSize: 12, color: theme.color.accentText, background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 8 }}
          >
            {showAbacasClients ? "▾ Hide" : "▸ Show"} {data.abacasClientCount} direct client{data.abacasClientCount !== 1 ? "s" : ""}
          </button>
        )}
        {showAbacasClients && data.abacasClients.length > 0 && (
          <div style={{ ...card, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--c-line)" }}>
                  <th style={colLeft}>Client</th>
                  <th style={colHead}>Tier</th>
                  <th style={colHead}>Platform Fee</th>
                  <th style={colHead}>Consultancy Revenue</th>
                  <th style={colHead}>Total Billed</th>
                </tr>
              </thead>
              <tbody>
                {data.abacasClients.map((cl) => (
                  <tr key={cl.id} style={{ borderBottom: `1px solid ${theme.color.border}` }}>
                    <td style={{ ...colCell, textAlign: "left", color: theme.color.heading, fontWeight: 600 }}>{cl.name}</td>
                    <td style={{ ...colCell, color: theme.color.faint }}>{cl.tier}</td>
                    <td style={{ ...colCell, color: theme.color.successText }}>{fmtGbp(cl.abacasRevenuePence)}</td>
                    <td style={{ ...colCell, color: theme.color.consultant }}>{fmtGbp(cl.consultancyRevenuePence)}</td>
                    <td style={{ ...colCell, color: theme.color.text }}>{fmtGbp(cl.totalBilledPence)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Third-party consultancy breakdown ── */}
      <div>
        <div style={sectionLabel}>Third-Party Consultancy Revenue — {period}</div>
        <div style={{ fontSize: 12, color: theme.color.faint, marginBottom: 12 }}>
          Time billing earned by other consultancy firms from their own clients.
        </div>
      </div>

      {/* Per-consultancy breakdown */}
      <div style={{ ...card, overflow: "auto", marginTop: -16 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--c-line)" }}>
              <th style={colLeft}>Consultancy</th>
              <th style={colHead}>Clients</th>
              <th style={colHead}>Abacas Revenue</th>
              <th style={colHead}>Their Revenue</th>
              <th style={colHead}>Total Billed</th>
              <th style={colHead}></th>
            </tr>
          </thead>
          <tbody>
            {data.consultancies.length === 0 && (
              <tr><td colSpan={6} style={{ ...colCell, textAlign: "center", color: theme.color.faint, padding: "32px 12px" }}>No third-party consultancies yet.</td></tr>
            )}
            {data.consultancies.map((c) => (
              <>
                <tr
                  key={c.id}
                  style={{ borderBottom: expanded.has(c.id) ? "none" : `1px solid ${theme.color.border}`, cursor: "pointer" }}
                  onClick={() => toggle(c.id)}
                  onMouseEnter={(e) => (e.currentTarget.style.background = theme.color.accentBg)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <td style={{ ...colCell, textAlign: "left", fontWeight: 600, color: theme.color.heading }}>
                    {expanded.has(c.id) ? "▾ " : "▸ "}{c.name}
                  </td>
                  <td style={{ ...colCell, color: theme.color.text }}>{c.clientCount}</td>
                  <td style={{ ...colCell, color: theme.color.successText }}>{fmtGbp(c.abacasRevenuePence)}</td>
                  <td style={{ ...colCell, color: theme.color.consultant }}>{fmtGbp(c.consultancyRevenuePence)}</td>
                  <td style={{ ...colCell, color: theme.color.heading, fontWeight: 600 }}>{fmtGbp(c.totalBilledPence)}</td>
                  <td style={colCell}></td>
                </tr>
                {expanded.has(c.id) && c.clients.map((cl) => (
                  <tr key={cl.id} style={{ borderBottom: `1px solid ${theme.color.border}`, background: theme.color.panel }}>
                    <td style={{ ...colCell, textAlign: "left", color: theme.color.subtle, paddingLeft: 32 }}>
                      {cl.name}
                      <span style={{ marginLeft: 8, fontSize: 11, color: theme.color.faint }}>{cl.tier}</span>
                    </td>
                    <td style={{ ...colCell, fontSize: 11, color: theme.color.faint }}>{cl.frameworks.join(", ") || "—"}</td>
                    <td style={{ ...colCell, color: theme.color.successText }}>{fmtGbp(cl.abacasRevenuePence)}</td>
                    <td style={{ ...colCell, color: theme.color.consultant }}>{fmtGbp(cl.consultancyRevenuePence)}</td>
                    <td style={{ ...colCell, color: theme.color.text }}>{fmtGbp(cl.totalBilledPence)}</td>
                    <td style={colCell}></td>
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Tab: Invites ──────────────────────────────────────────────────────────

function PlatformInvitesTab() {
  const [invites, setInvites] = useState<InviteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [showInviteAuditor, setShowInviteAuditor] = useState(false);

  function load() {
    setLoading(true);
    api.get<{ invites: InviteSummary[] }>("/invites")
      .then((d) => setInvites(d.invites))
      .catch(() => {})
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  const filtered = search
    ? invites.filter((i) =>
        i.email.toLowerCase().includes(search.toLowerCase()) ||
        (i.consultancyName ?? "").toLowerCase().includes(search.toLowerCase()) ||
        (i.organizationName ?? "").toLowerCase().includes(search.toLowerCase())
      )
    : invites;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search invites" />
        <button onClick={() => setShowInviteAuditor(true)} style={{ ...btnPrimary, fontSize: 12, flexShrink: 0 }}>
          Invite auditor
        </button>
      </div>
      <div style={{ fontSize: 12, color: theme.color.subtle, margin: "0 0 16px" }}>
        Consultancies, clients, staff and auditors invited across the platform. Auditors are invited into an existing certification bureau.
      </div>
      {loading && <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading</div>}
      {!loading && <InviteHistoryTable invites={filtered} onResent={load} onRevoke={revokeInvite} />}
      {showInviteAuditor && <InviteAuditorModal onClose={() => { setShowInviteAuditor(false); load(); }} />}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────

const ADMIN_TABS: AdminTab[] = ["overview", "consultancies", "clients", "consultants", "auditors", "revenue", "invites", "partners", "bureau-review", "bureau-changes", "standards-watch", "control-mappings", "bug-reports"];

export function PlatformAdminPage() {
  // ?tab=bug-reports&ticket=<id> is the link the new-report email carries.
  const [params] = useSearchParams();
  const [tab, setTab] = useState<AdminTab>(() => {
    const t = params.get("tab") as AdminTab | null;
    return t && ADMIN_TABS.includes(t) ? t : "overview";
  });
  const [initialTicketId] = useState(() => params.get("ticket"));
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [bugSummary, setBugSummary] = useState<{ open: number; new: number } | null>(null);

  function loadStats() {
    api.get<PlatformStats>("/admin/stats").then(setStats).catch(() => {});
  }
  useEffect(loadStats, []);
  const loadBugSummary = useCallback(() => {
    api.get<{ open: number; new: number }>("/admin/bug-reports/summary").then(setBugSummary).catch(() => {});
  }, []);
  useEffect(loadBugSummary, [loadBugSummary]);
  // Bureau name / accreditation changes awaiting a decision (?tab=bureau-changes is the email's link).
  const [bureauChangesPending, setBureauChangesPending] = useState(0);
  const loadBureauChanges = useCallback(() => {
    api.get<{ requests: unknown[] }>("/admin/bureau-change-requests").then((d) => setBureauChangesPending(d.requests.length)).catch(() => {});
  }, []);
  useEffect(loadBureauChanges, [loadBureauChanges]);

  const TAB_LABELS: Record<AdminTab, string> = {
    overview: "Overview",
    consultancies: "Consultancies",
    clients: "Clients",
    consultants: "Consultants",
    auditors: "Auditors",
    revenue: "Revenue",
    invites: "Invitations",
    partners: "Partners",
    "bureau-review": "Bureau Review",
    "bureau-changes": "Bureau Changes",
    "standards-watch": "Standards Watch",
    "control-mappings": "Control mappings",
    "bug-reports": "Bug Reports",
  };

  // Stat tile definitions — no longer used as primary nav; clicking goes to relevant tab
  const statTiles = stats ? [
    {
      value: stats.clients,
      label: "Client orgs on platform",
      sub: stats.compliantClients > 0 || stats.partialClients > 0 || stats.atRiskClients > 0
        ? `${stats.compliantClients} compliant · ${stats.partialClients} partial · ${stats.atRiskClients} at risk`
        : `${stats.neverAssessedClients} not yet assessed`,
      color: theme.color.successText,
      bg: cbSafe("rgba(22,163,74,0.07)"),
      border: cbSafe("rgba(22,163,74,0.3)"),
      onClick: () => setTab("clients"),
    },
    {
      value: stats.consultancies,
      label: "Consultancy firms",
      sub: `${stats.consultants} consultant${stats.consultants !== 1 ? "s" : ""}`,
      color: theme.color.accentText,
      bg: theme.color.accentBg,
      border: theme.color.accent + "55",
      onClick: () => setTab("consultancies"),
    },
    {
      value: stats.consultants,
      label: "Consultants",
      sub: `${stats.auditors} auditor${stats.auditors !== 1 ? "s" : ""}`,
      color: theme.color.consultant,
      bg: theme.color.consultantBg,
      border: theme.color.consultant + "55",
      onClick: () => setTab("consultants"),
    },
    {
      value: stats.attentionCount,
      label: "Needs attention",
      sub: stats.attentionCount === 0
        ? "All clients on track"
        : `${stats.atRiskClients} critical · ${stats.neverAssessedClients} unassessed`,
      color: stats.attentionCount > 0 ? theme.color.dangerText : theme.color.successText,
      bg: stats.attentionCount > 0 ? cbSafe("rgba(220,38,38,0.07)") : cbSafe("rgba(22,163,74,0.07)"),
      border: stats.attentionCount > 0 ? cbSafe("rgba(220,38,38,0.3)") : cbSafe("rgba(22,163,74,0.25)"),
      onClick: () => setTab("overview"),
    },
  ] : [];

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <div style={{
        padding: "20px 32px 0", flexShrink: 0,
        background: theme.color.panel,
      }}>
        {/* Identity row */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: theme.color.heading, letterSpacing: "-0.025em" }}>
                Platform Administration
              </div>
              <span style={{
                fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
                background: cbSafe("rgba(239,68,68,.1)"), color: theme.color.dangerText,
                textTransform: "uppercase" as const, letterSpacing: "0.07em", border: cbSafe("1px solid rgba(239,68,68,.2)"),
              }}>
                Owner
              </span>
            </div>
            <div style={{ fontSize: 11, color: theme.color.faint, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" }}>
              abacassecurity.onmicrosoft.com
            </div>
          </div>
        </div>

        {/* Stat tiles */}
        {stats && (
          <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
            {statTiles.map((tile) => (
              <div
                key={tile.label}
                onClick={tile.onClick}
                style={{
                  flex: 1, padding: "14px 18px", cursor: "pointer", borderRadius: 3,
                  background: tile.bg,
                  border: `1px solid ${tile.border}`,
                  borderLeft: `4px solid ${tile.color}`,
                  transition: "opacity 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                <div style={{ fontSize: 28, fontWeight: 800, color: tile.color, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                  {tile.value}
                </div>
                <div style={{ fontSize: 11, color: tile.color, marginTop: 4, fontWeight: 600 }}>
                  {tile.label}
                </div>
                {tile.sub && (
                  <div style={{ fontSize: 10, color: tile.color, marginTop: 3, opacity: 0.75 }}>
                    {tile.sub}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Tab navigation */}
        <IndexTabs
          items={ADMIN_TABS.map((t) => ({
            id: t,
            // These two counts keep their title on their own tag; IndexTabs' count carries no title.
            label: (
              <>
                {TAB_LABELS[t]}
                {t === "bureau-changes" && bureauChangesPending > 0 && (
                  <span title={`${bureauChangesPending} awaiting review`} style={countTag}>{bureauChangesPending}</span>
                )}
                {t === "bug-reports" && bugSummary && bugSummary.new > 0 && (
                  <span title={`${bugSummary.new} new, ${bugSummary.open} open`} style={countTag}>{bugSummary.new}</span>
                )}
              </>
            ),
            count: t === "overview" && stats && stats.attentionCount > 0 ? stats.attentionCount : undefined,
          }))}
          value={tab}
          onChange={setTab}
          ground="var(--c-panel)"
          style={{ marginBottom: 0 }}
        />
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflow: "auto", padding: "24px 32px" }}>
        {tab === "overview"      && stats && <OverviewTab stats={stats} onNavigate={setTab} />}
        {tab === "overview"      && !stats && <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>}
        {tab === "consultancies" && <ConsultanciesTab onRefreshStats={loadStats} />}
        {tab === "clients"       && <ClientsTab />}
        {tab === "consultants"   && <ConsultantsTab />}
        {tab === "auditors"      && <AuditorsTab />}
        {tab === "revenue"       && <RevenueTab />}
        {tab === "invites"       && <PlatformInvitesTab />}
        {tab === "partners"      && <PartnersTab />}
        {tab === "bureau-review" && <BureauReviewTab />}
        {tab === "bureau-changes" && <BureauChangeRequestsAdminTab onCountsChanged={loadBureauChanges} />}
        {tab === "standards-watch" && <StandardsWatchTab />}
        {tab === "control-mappings" && <ControlMappingsAdminTab />}
        {tab === "bug-reports" && <BugReportsAdminTab initialTicketId={initialTicketId} onCountsChanged={loadBugSummary} />}
      </div>
    </div>
  );
}

// ── Bureau Review tab — approve/suspend consultancy-owned bureaus ──────────

interface PendingBureau {
  id: string;
  name: string;
  accreditationBody: string | null;
  accreditation: string | null;
  country: string | null;
  contactEmail: string | null;
  bureauStatus: string;
  ownedByConsultancyId: string | null;
  consultancyLinks: Array<{ consultancy: { id: string; name: string } }>;
}

function BureauReviewTab() {
  const [bureaus, setBureaus] = useState<PendingBureau[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState<string | null>(null);

  function load() {
    setLoading(true);
    api.get<{ bureaus: PendingBureau[] }>("/admin/bureaus")
      .then(d => setBureaus(d.bureaus))
      .catch(() => setError("Failed to load bureau review list."))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  async function setStatus(bureauId: string, status: "active" | "suspended" | "pending_review") {
    setUpdating(bureauId);
    try {
      await api.patch(`/admin/bureaus/${bureauId}/status`, { status });
      load();
    } catch {
      setError("Failed to update bureau status.");
    } finally {
      setUpdating(null);
    }
  }

  const statusBadge = (status: string) => {
    const map: Record<string, { label: string; color: string; bg: string }> = {
      active:         { label: "Approved",        color: cbSafe("#1b5e20"), bg: cbSafe("#e8f5e9") },
      pending_review: { label: "Pending Review",  color: "#e65100", bg: "#fff3e0" },
      suspended:      { label: "Suspended",       color: cbSafe("#b71c1c"), bg: cbSafe("#ffebee") },
    };
    const s = map[status] ?? { label: status, color: theme.color.muted, bg: theme.color.panel };
    return (
      <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 3, background: s.bg, color: s.color }}>
        {s.label}
      </span>
    );
  };

  if (loading) return <div style={{ fontSize: 13, color: theme.color.muted }}>Loading…</div>;
  if (error)   return <div style={{ fontSize: 13, color: cbSafe("#e53935") }}>{error}</div>;

  const pending   = bureaus.filter(b => b.bureauStatus === "pending_review");
  const others    = bureaus.filter(b => b.bureauStatus !== "pending_review");

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>Bureau Review</div>
      <div style={{ fontSize: 13, color: theme.color.muted, marginBottom: 20, lineHeight: 1.5 }}>
        Consultancy-owned bureaus created via the self-serve Auditor Accreditation flow. Approve a bureau before its staff can access the Auditor Portal.
      </div>

      {pending.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, marginBottom: 10 }}>Pending Approval</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {pending.map(b => (
              <div key={b.id} style={{ ...card, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, marginBottom: 4 }}>
                    {b.name}
                    <span style={{ marginLeft: 10 }}>{statusBadge(b.bureauStatus)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: theme.color.muted, marginBottom: 2 }}>
                    {b.consultancyLinks.map(l => l.consultancy.name).join(", ")}
                    {b.accreditationBody ? ` · ${b.accreditationBody}` : ""}
                    {b.accreditation ? ` — ${b.accreditation}` : ""}
                  </div>
                  {b.contactEmail && <div style={{ fontSize: 12, color: theme.color.muted }}>{b.contactEmail}</div>}
                  {b.country && <div style={{ fontSize: 12, color: theme.color.muted }}>{b.country}</div>}
                </div>
                <div style={{ display: "flex", gap: 8, flexShrink: 0, marginLeft: 16 }}>
                  <button
                    disabled={updating === b.id}
                    onClick={() => setStatus(b.id, "active")}
                    style={{ ...btnPrimary, padding: "6px 14px", fontSize: 12 }}
                  >
                    Approve
                  </button>
                  <button
                    disabled={updating === b.id}
                    onClick={() => setStatus(b.id, "suspended")}
                    style={{
                      padding: "6px 14px", fontSize: 12, cursor: "pointer",
                      borderRadius: 3, border: cbSafe("1px solid #ef9a9a"),
                      background: cbSafe("#ffebee"), color: cbSafe("#c62828"),
                    }}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {pending.length === 0 && (
        <div style={{ fontSize: 13, color: theme.color.subtle, marginBottom: 24 }}>No bureaus pending review.</div>
      )}

      {others.length > 0 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, marginBottom: 10 }}>All Consultancy Bureaus</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {others.map(b => (
              <div key={b.id} style={{ ...card, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: theme.color.heading, marginBottom: 2 }}>
                    {b.name}
                    <span style={{ marginLeft: 10 }}>{statusBadge(b.bureauStatus)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: theme.color.muted }}>
                    {b.consultancyLinks.map(l => l.consultancy.name).join(", ")}
                    {b.accreditationBody ? ` · ${b.accreditationBody}` : ""}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexShrink: 0, marginLeft: 16 }}>
                  {b.bureauStatus === "active" && (
                    <button onClick={() => setStatus(b.id, "suspended")} disabled={updating === b.id} style={{
                      padding: "5px 12px", fontSize: 12, cursor: "pointer",
                      borderRadius: 3, border: cbSafe("1px solid #ef9a9a"), background: cbSafe("#ffebee"), color: cbSafe("#c62828"),
                    }}>
                      Suspend
                    </button>
                  )}
                  {b.bureauStatus === "suspended" && (
                    <button onClick={() => setStatus(b.id, "active")} disabled={updating === b.id} style={{ ...btnPrimary, padding: "5px 12px", fontSize: 12 }}>
                      Re-approve
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Partners tab — partner applications and partner tier / status ─────────
// The platform owner's side of /partner (routes/partner.routes.ts, platform
// administrators only): review applications from the public form, approve one
// into a consultancy or reject it (a rejected application stays rejected), and
// set an approved partner's tier or status. Every action is audited server-side.

interface PartnerApplication {
  id: string;
  firmName: string;
  contactName: string;
  contactEmail: string;
  website: string | null;
  description: string;
  expectedClients: number | null;
  requestedTier: string;
  status: "pending" | "approved" | "rejected";
  reviewNotes: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

interface PartnerConsultancy {
  id: string;
  name: string;
  slug: string;
  partnerTier: string | null;
  partnerStatus: string | null;
  platformOwner: boolean;
  contactEmail: string | null;
  _count: { organizations: number };
}

/** GET /partner/applications/:id: the application with the consultancy it created and who reviewed it. */
interface PartnerApplicationDetail extends PartnerApplication {
  updatedAt: string;
  consultancy: { id: string; name: string; slug: string; partnerTier: string | null; partnerStatus: string | null } | null;
  reviewer: { displayName: string | null; email: string } | null;
}

/** The full record of one partner application, read from the detail route each time it opens. */
function PartnerApplicationDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const [detail, setDetail] = useState<PartnerApplicationDetail | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError("");
    api.get<{ application: PartnerApplicationDetail }>(`/partner/applications/${id}`)
      .then((d) => { if (!cancelled) setDetail(d.application); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load the application."); });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const when = (iso: string | null) => iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
  const row = (label: string, value: React.ReactNode) => (
    <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 12, padding: "7px 0", borderBottom: `1px solid ${theme.color.border}`, fontSize: 12.5 }}>
      <div style={{ color: theme.color.muted }}>{label}</div>
      <div style={{ color: theme.color.text, minWidth: 0, overflowWrap: "anywhere" }}>{value}</div>
    </div>
  );

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", justifyContent: "flex-end" }}>
      <div role="dialog" aria-modal="true" aria-label="Partner application" onClick={(e) => e.stopPropagation()}
        style={{ ...floatingSheet, width: "min(520px, 100%)", height: "100%", overflowY: "auto", padding: 24, fontFamily: theme.font }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid var(--c-line)" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.color.heading, flex: 1 }}>{detail?.firmName ?? "Partner application"}</div>
          <button onClick={onClose} style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "5px 12px", fontSize: 12, color: theme.color.muted, cursor: "pointer" }}>Close</button>
        </div>
        {error && <div role="alert" style={{ fontSize: 13, color: theme.color.dangerText }}>{error}</div>}
        {!detail && !error && <div style={{ fontSize: 13, color: theme.color.muted }}>Loading…</div>}
        {detail && (
          <>
            {row("Status", detail.status === "pending" ? "Pending" : detail.status === "approved" ? "Approved" : "Rejected")}
            {row("Contact", detail.contactName)}
            {row("Email", detail.contactEmail)}
            {row("Website", detail.website ? <a href={safeHref(detail.website)} target="_blank" rel="noopener noreferrer" style={{ color: theme.color.accentText }}>{detail.website}</a> : "—")}
            {row("Requested tier", detail.requestedTier)}
            {row("Expected clients", detail.expectedClients ?? "—")}
            {row("Applied", when(detail.createdAt))}
            {row("Last updated", when(detail.updatedAt))}
            {row("Reviewed", detail.reviewedAt ? `${when(detail.reviewedAt)}${detail.reviewer ? ` by ${detail.reviewer.displayName || detail.reviewer.email}` : ""}` : "Not yet reviewed")}
            {row("Review note", detail.reviewNotes || "—")}
            {row("Consultancy", detail.consultancy
              ? `${detail.consultancy.name} (${detail.consultancy.slug}) · tier ${detail.consultancy.partnerTier ?? "none"} · ${PARTNER_STATUS_LABELS[detail.consultancy.partnerStatus ?? ""] ?? detail.consultancy.partnerStatus ?? "no status"}`
              : detail.status === "approved" ? "Consultancy since removed" : "None created")}
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, marginTop: 16, marginBottom: 6 }}>About the firm</div>
            <div style={{ fontSize: 12.5, color: theme.color.text, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{detail.description}</div>
          </>
        )}
      </div>
    </div>
  );
}

const PARTNER_TIERS = ["starter", "growth", "enterprise"] as const;
const PARTNER_STATUSES = ["active", "suspended", "pending_review"] as const;
const PARTNER_STATUS_LABELS: Record<string, string> = { active: "Active", suspended: "Suspended", pending_review: "Pending review" };
const slugify = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 100);

function PartnersTab() {
  const [applications, setApplications] = useState<PartnerApplication[]>([]);
  const [partners, setPartners] = useState<PartnerConsultancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [filter, setFilter] = useState<"pending" | "all">("pending");
  // The application being approved: the slug and tier the new consultancy gets.
  const [approving, setApproving] = useState<{ id: string; slug: string; tier: string } | null>(null);
  // The application open in the detail drawer.
  const [viewing, setViewing] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get<{ applications: PartnerApplication[] }>("/partner/applications"),
      api.get<{ consultancies: PartnerConsultancy[] }>("/partner/consultancies"),
    ])
      .then(([a, c]) => { setApplications(a.applications); setPartners(c.consultancies); setError(""); })
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load partners."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  async function approve() {
    if (!approving) return;
    setBusy(approving.id);
    try {
      await api.post(`/partner/applications/${approving.id}/approve`, { slug: approving.slug, partnerTier: approving.tier });
      setApproving(null);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Approval failed.");
    } finally {
      setBusy(null);
    }
  }

  async function reject(a: PartnerApplication) {
    const notes = window.prompt(`Reject the application from ${a.firmName}? Add a note for the record (optional).`, "");
    if (notes === null) return;
    setBusy(a.id);
    try {
      await api.post(`/partner/applications/${a.id}/reject`, notes.trim() ? { reviewNotes: notes.trim() } : {});
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Rejection failed.");
    } finally {
      setBusy(null);
    }
  }

  async function updatePartner(c: PartnerConsultancy, patch: { partnerTier?: string; partnerStatus?: string }) {
    setBusy(c.id);
    try {
      await api.patch(`/partner/consultancies/${c.id}`, patch);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Update failed.");
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <div style={{ fontSize: 13, color: theme.color.muted }}>Loading…</div>;

  const shown = filter === "pending" ? applications.filter((a) => a.status === "pending") : applications;
  const partnerFirms = partners.filter((c) => !c.platformOwner && (c.partnerTier || c.partnerStatus));
  const fmt = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
      {error && <div role="alert" style={{ fontSize: 13, color: theme.color.dangerText }}>{error}</div>}
      {viewing && <PartnerApplicationDrawer id={viewing} onClose={() => setViewing(null)} />}

      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading }}>Partner applications</div>
          <select value={filter} onChange={(e) => setFilter(e.target.value as "pending" | "all")} aria-label="Which applications"
            style={{ ...inputStyle, width: 160, padding: "4px 8px", fontSize: 12, marginLeft: "auto" }}>
            <option value="pending">Pending only</option>
            <option value="all">All applications</option>
          </select>
        </div>
        <div style={{ fontSize: 13, color: theme.color.muted, marginBottom: 14, lineHeight: 1.5 }}>
          Applications from the public partner form. Approving one creates the consultancy; invite its first consultant from Invitations.
        </div>
        {shown.length === 0 && <div style={{ fontSize: 13, color: theme.color.subtle }}>{filter === "pending" ? "No applications waiting." : "No applications yet."}</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {shown.map((a) => (
            <div key={a.id} style={{ ...card, padding: "16px 20px" }}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: theme.color.heading }}>
                    <button onClick={() => setViewing(a.id)} title="Open the full application"
                      style={{ background: "none", border: "none", padding: 0, font: "inherit", color: "inherit", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>
                      {a.firmName}
                    </button>
                    <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: theme.color.muted }}>
                      {a.status === "pending" ? "Pending" : a.status === "approved" ? "Approved" : "Rejected"}
                      {a.reviewedAt ? ` ${fmt(a.reviewedAt)}` : ""}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: theme.color.muted, marginTop: 2 }}>
                    {a.contactName} · {a.contactEmail}
                    {a.website && <> · <a href={safeHref(a.website)} target="_blank" rel="noopener noreferrer" style={{ color: theme.color.accentText }}>{a.website}</a></>}
                  </div>
                  <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2 }}>
                    Requested tier {a.requestedTier}{a.expectedClients ? ` · about ${a.expectedClients} clients` : ""} · applied {fmt(a.createdAt)}
                  </div>
                  <div style={{ fontSize: 12.5, color: theme.color.text, marginTop: 8, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{a.description}</div>
                  {a.reviewNotes && <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 6 }}>Review note: {a.reviewNotes}</div>}
                </div>
                {a.status === "pending" && approving?.id !== a.id && (
                  <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                    <button disabled={busy === a.id} onClick={() => setApproving({ id: a.id, slug: slugify(a.firmName), tier: a.requestedTier })}
                      style={{ ...btnPrimary, padding: "6px 14px", fontSize: 12 }}>Approve…</button>
                    <button disabled={busy === a.id} onClick={() => reject(a)}
                      style={{ padding: "6px 14px", fontSize: 12, cursor: "pointer", borderRadius: 3, border: `1px solid ${theme.color.danger}`, background: theme.color.dangerBg, color: theme.color.dangerText }}>Reject</button>
                  </div>
                )}
              </div>
              {approving?.id === a.id && (
                <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginTop: 12, paddingTop: 12, borderTop: `1px solid ${theme.color.border}` }}>
                  <label style={{ fontSize: 11, color: theme.color.muted, display: "flex", flexDirection: "column", gap: 3 }}>
                    Consultancy slug
                    <input value={approving.slug} onChange={(e) => setApproving({ ...approving, slug: e.target.value.toLowerCase() })} style={{ ...inputStyle, width: 220 }} />
                  </label>
                  <label style={{ fontSize: 11, color: theme.color.muted, display: "flex", flexDirection: "column", gap: 3 }}>
                    Partner tier
                    <select value={approving.tier} onChange={(e) => setApproving({ ...approving, tier: e.target.value })} style={{ ...inputStyle, width: 150 }}>
                      {PARTNER_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </label>
                  <button disabled={busy === a.id || !/^[a-z0-9-]+$/.test(approving.slug)} onClick={approve} style={{ ...btnPrimary, padding: "7px 14px", fontSize: 12 }}>
                    {busy === a.id ? "Approving…" : "Approve and create consultancy"}
                  </button>
                  <button onClick={() => setApproving(null)} style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "7px 12px", fontSize: 12, color: theme.color.muted, cursor: "pointer" }}>Cancel</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>Partner consultancies</div>
        <div style={{ fontSize: 13, color: theme.color.muted, marginBottom: 14 }}>Each partner's tier and standing. Suspending a partner is recorded; it does not remove the firm.</div>
        {partnerFirms.length === 0 && <div style={{ fontSize: 13, color: theme.color.subtle }}>No partner consultancies yet.</div>}
        {partnerFirms.length > 0 && (
          <div style={{ ...card, padding: 0 }}>
            {partnerFirms.map((c, i) => (
              <div key={c.id} style={{ display: "flex", gap: 12, alignItems: "center", padding: "10px 16px", flexWrap: "wrap", borderBottom: i < partnerFirms.length - 1 ? `1px solid ${theme.color.border}` : "none" }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{c.name}</div>
                  <div style={{ fontSize: 11.5, color: theme.color.faint }}>{c.slug} · {c._count.organizations} client{c._count.organizations !== 1 ? "s" : ""}{c.contactEmail ? ` · ${c.contactEmail}` : ""}</div>
                </div>
                <select aria-label={`Partner tier for ${c.name}`} value={c.partnerTier ?? ""} disabled={busy === c.id}
                  onChange={(e) => updatePartner(c, { partnerTier: e.target.value })} style={{ ...inputStyle, width: 130, padding: "4px 8px", fontSize: 12 }}>
                  {!c.partnerTier && <option value="">No tier</option>}
                  {PARTNER_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <select aria-label={`Partner status for ${c.name}`} value={c.partnerStatus ?? ""} disabled={busy === c.id}
                  onChange={(e) => updatePartner(c, { partnerStatus: e.target.value })} style={{ ...inputStyle, width: 150, padding: "4px 8px", fontSize: 12 }}>
                  {!c.partnerStatus && <option value="">No status</option>}
                  {PARTNER_STATUSES.map((st) => <option key={st} value={st}>{PARTNER_STATUS_LABELS[st]}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
